import { useTranslation } from "react-i18next";
import SeoHead from "@/components/SeoHead";
import Header from "@/components/Header";
import PageBreadcrumb from "@/components/PageBreadcrumb";

import Footer from "@/components/Footer";
import CallToAction from "@/components/CallToAction";
import Section from "@/components/Section";
import ServiceSection, { ServiceSectionData } from "@/components/ServiceSection";
import ServicesSideNav from "@/components/ServicesSideNav";
import { Target, ShieldCheck, Rocket, HeartHandshake, LucideIcon } from "lucide-react";

const serviceMeta: Array<{ id: string; index: number; icon: LucideIcon }> = [
  { id: "strategy", index: 1, icon: Target },
  { id: "governance", index: 2, icon: ShieldCheck },
  { id: "implementation", index: 3, icon: Rocket },
  { id: "support", index: 4, icon: HeartHandshake },
];

const Services = () => {
  const { t } = useTranslation();

  const services: ServiceSectionData[] = serviceMeta.map((m) => ({
    id: m.id,
    index: m.index,
    icon: m.icon,
    eyebrowLabel: t(`servicesPage.items.${m.id}.eyebrowLabel`),
    title: t(`servicesPage.items.${m.id}.title`),
    duration: t(`servicesPage.items.${m.id}.duration`),
    complexity: t(`servicesPage.items.${m.id}.complexity`),
    intro: t(`servicesPage.items.${m.id}.intro`),
    serviceDescription: t(`servicesPage.items.${m.id}.serviceDescription`),
    explorationPoints: t(`servicesPage.items.${m.id}.explorationPoints`, {
      returnObjects: true,
    }) as string[],
    process: t(`servicesPage.items.${m.id}.process`),
    result: t(`servicesPage.items.${m.id}.result`),
  }));

  const navItems = services.map((s) => ({
    id: s.id,
    label: s.title,
    icon: s.icon,
  }));

  const serviceJsonLd = services.map((s) => ({
    "@context": "https://schema.org",
    "@type": "Service",
    name: s.title,
    description: s.serviceDescription,
    provider: {
      "@type": "Organization",
      name: "AI-labbet",
      url: "https://ailabbet.com/",
    },
    areaServed: "SE",
    url: `https://ailabbet.com/services#${s.id}`,
  }));

  return (
    <>
      <SeoHead
        title={{
          en: "Our Services | AI-labbet",
          sv: "Våra tjänster | AI-labbet",
        }}
        description={{
          en: "Explore AI-labbet's services: AI strategy consulting, governance frameworks, implementation support, and ongoing coaching.",
          sv: "Utforska AI-labbets tjänster: rådgivning kring AI-strategi, styrningsramverk, implementationsstöd och löpande coaching.",
        }}
        path="/services"
        jsonLd={serviceJsonLd}
      />

      <div className="min-h-screen bg-background">
        <Header />
        <PageBreadcrumb currentPage="services" />

        <main>
          <Section
            id="services-top"
            title={<>{t("servicesPage.titlePre")} <span>{t("servicesPage.titleAccent")}</span></>}
            titleAs="h1"
            containerSize="wide"
            grid
            className="overflow-visible"
          >
            <div className="lg:grid lg:grid-cols-[18rem_1fr] lg:gap-16 lg:items-start">
              <ServicesSideNav items={navItems} variant="sticky" />

              <div>
                <ServicesSideNav items={navItems} variant="inline" />
                {services.map((service) => (
                  <ServiceSection key={service.id} service={service} />
                ))}
              </div>
            </div>
          </Section>

          <CallToAction />
        </main>
        <Footer />
      </div>
    </>
  );
};

export default Services;
